import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ChevronDown, MoreVertical } from "lucide-react";
import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";
import { handleError } from "src/Common/function";
import Loader from "src/Components/Common/Loader";
import UserNotificationService from "src/Services/API/usernotification.service";
import {
  connectUserNotificationSocket,
  disconnectUserNotificationSocket,
} from "src/Services/socket/userNotificationSocket";
import { requestUnreadCountRefresh } from "src/hooks/useNotificationUnreadCount";
import NotificationEmptyState from "./NotificationEmptyState";
import {
  formatRelativeTime,
  getNotificationPath,
  getRenderer,
  isRenderable,
  NOTIFICATION_TYPE_OPTIONS,
} from "./notificationRenderers";

const PAGE_SIZE = 10;
const ALL_TYPES = "all";

const TABS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
];

// Menu surface from the design: 16px radius over a soft drop shadow and the
// hairline inset ring that keeps it legible on a white card.
const MENU_SURFACE =
  "rounded-2xl bg-white shadow-[0px_2px_8px_rgba(0,0,0,0.08),inset_0px_0px_1px_rgba(0,0,0,0.30)]";
const MENU_ITEM =
  "w-full min-h-[36px] px-3 py-[6px] flex items-center gap-3 rounded-lg text-left font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize";

// The gear from the design, which repeats the header's settings glyph.
const SettingsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="15" viewBox="0 0 14 15" fill="none">
    <path
      d="M7.49127 1.83333C7.49127 1.61232 7.40341 1.40042 7.24712 1.24414C7.09085 1.08792 6.8789 1 6.65793 1H6.36496C6.14399 1 5.93204 1.08792 5.77577 1.24414C5.61949 1.40042 5.53163 1.61232 5.53163 1.83333V1.95378C5.5313 2.27516 5.44631 2.59087 5.28554 2.86914C5.12487 3.14709 4.89352 3.37769 4.61561 3.53841L4.32916 3.70573L4.32785 3.70638C4.0492 3.86721 3.73292 3.95182 3.41119 3.95182C3.08946 3.95178 2.77315 3.86724 2.49452 3.70638L2.40923 3.66146C2.40437 3.65886 2.39969 3.65575 2.39491 3.65299C2.20375 3.54275 1.97659 3.5127 1.7634 3.56966C1.55022 3.62667 1.3682 3.76612 1.25754 3.95703L1.11106 4.21029L1.11041 4.20964C1.00019 4.40082 0.970716 4.62861 1.02772 4.8418C1.07765 5.02831 1.19073 5.19091 1.34608 5.30273L1.41509 5.34766L1.44244 5.36393L1.54205 5.43034C1.80686 5.58944 2.02787 5.8127 2.18332 6.08008C2.34403 6.35659 2.42942 6.67041 2.43137 6.99023V7.33138L2.42811 7.45247C2.41064 7.73333 2.3286 8.00702 2.18788 8.25195C2.02691 8.53205 1.79448 8.76491 1.5147 8.92643L1.51405 8.92578L1.42225 8.98177L1.41509 8.98633L1.41444 8.98568C1.22377 9.09632 1.08473 9.27857 1.02772 9.49154C0.970765 9.70454 1.00042 9.93197 1.11041 10.123L1.25754 10.3763C1.3682 10.5672 1.55022 10.7067 1.7634 10.7637C1.97659 10.8206 2.20375 10.7906 2.39491 10.6803L2.40923 10.6719L2.49452 10.627C2.77315 10.4661 3.08946 10.3816 3.41119 10.3815C3.73292 10.3815 4.0492 10.4661 4.32785 10.627L4.32916 10.6276L4.61496 10.7936L4.71718 10.8574C4.95004 11.0131 5.14481 11.2207 5.28554 11.4642C5.44631 11.7425 5.5313 12.0582 5.53163 12.3796V12.5C5.53163 12.721 5.61949 12.9329 5.77577 13.0892C5.93204 13.2454 6.14399 13.3333 6.36496 13.3333H6.65793C6.8789 13.3333 7.09085 13.2454 7.24712 13.0892C7.40341 12.9329 7.49127 12.721 7.49127 12.5V12.3796C7.4916 12.0582 7.57658 11.7425 7.73736 11.4642C7.89823 11.1859 8.12956 10.9544 8.40793 10.7936L8.69374 10.6276L8.69504 10.627C8.97369 10.4661 9.28997 10.3815 9.61171 10.3815C9.93344 10.3816 10.2497 10.4661 10.5284 10.627L10.6137 10.6719L10.628 10.6803C10.8191 10.7906 11.0463 10.8206 11.2595 10.7637C11.4727 10.7067 11.6547 10.5672 11.7654 10.3763L11.9092 10.1211L11.9118 10.1165C12.022 9.92537 12.0522 9.69818 11.9952 9.48503C11.9386 9.27335 11.8006 9.09263 11.6117 8.98177L11.5225 8.93424C11.5177 8.93168 11.5129 8.92915 11.5082 8.92643C11.2284 8.76491 10.996 8.53205 10.835 8.25195C10.6741 7.97193 10.5902 7.65433 10.5915 7.33138V7C10.5906 6.67773 10.6745 6.36084 10.835 6.08138C10.9759 5.83631 11.1713 5.62726 11.4053 5.4707L11.5082 5.4069L11.6006 5.35156L11.6078 5.347C11.7986 5.23637 11.9381 5.05484 11.9952 4.8418C12.0522 4.62853 12.0221 4.40086 11.9118 4.20964L11.7654 3.95703C11.6547 3.76612 11.4727 3.62667 11.2595 3.56966C11.0463 3.5127 10.8191 3.54275 10.628 3.65299C10.6232 3.65575 10.6185 3.65886 10.6137 3.66146L10.5277 3.70573C10.2491 3.86658 9.93343 3.95178 9.61171 3.95182C9.28997 3.95182 8.97369 3.86721 8.69504 3.70638L8.69374 3.70573L8.40663 3.53906C8.12895 3.37835 7.89793 3.14693 7.73736 2.86914C7.57658 2.59087 7.4916 2.27516 7.49127 1.95378V1.83333ZM8.49127 1.95312L8.49843 2.06185C8.51278 2.16976 8.54841 2.27428 8.60324 2.36914C8.65812 2.46406 8.73101 2.54697 8.81744 2.61328L8.90793 2.67383L8.90923 2.67448L9.19504 2.84049L9.2927 2.88867C9.39343 2.93037 9.50199 2.95182 9.61171 2.95182C9.7579 2.95178 9.90177 2.91359 10.0284 2.84049L10.0427 2.83203L10.143 2.77865C10.5606 2.54282 11.0542 2.48016 11.518 2.60417C11.9871 2.72964 12.3872 3.03615 12.6306 3.45638L12.7777 3.70964C13.0204 4.13028 13.0861 4.63047 12.9607 5.09961C12.8359 5.56635 12.5319 5.96456 12.115 6.20833L12.0154 6.26888L12.0082 6.27279C11.881 6.34621 11.7754 6.45212 11.7022 6.57943C11.629 6.70674 11.5909 6.8512 11.5915 6.99805V7.33529C11.5909 7.48213 11.629 7.62659 11.7022 7.75391C11.7746 7.87991 11.8789 7.98462 12.0043 8.05794L12.0935 8.10547L12.1085 8.11393C12.5286 8.3573 12.8352 8.75752 12.9607 9.22656C13.0857 9.69419 13.0204 10.1922 12.7797 10.612L12.6338 10.8724L12.6306 10.8776L12.6299 10.877C12.3865 11.2971 11.9871 11.6037 11.518 11.7292C11.0541 11.8532 10.5606 11.79 10.143 11.554L10.0427 11.5013C10.0378 11.4987 10.0332 11.4956 10.0284 11.4928C9.90177 11.4197 9.7579 11.3816 9.61171 11.3815C9.46553 11.3815 9.32166 11.4198 9.19504 11.4928L8.90923 11.6589L8.90793 11.6595C8.78143 11.7326 8.67636 11.8377 8.60324 11.9642C8.53016 12.0906 8.49147 12.2342 8.49127 12.3802V12.5C8.49127 12.9862 8.29797 13.4524 7.95416 13.7962C7.61035 14.14 7.14412 14.3333 6.65793 14.3333H6.36496C5.87877 14.3333 5.41255 14.14 5.06874 13.7962C4.72492 13.4524 4.53163 12.9862 4.53163 12.5V12.3802C4.53143 12.2342 4.49273 12.0906 4.41965 11.9642C4.34653 11.8377 4.24147 11.7326 4.11496 11.6595L4.11366 11.6589L3.82785 11.4928C3.70124 11.4198 3.55736 11.3815 3.41119 11.3815C3.265 11.3816 3.12113 11.4197 2.99452 11.4928C2.98972 11.4956 2.98509 11.4987 2.9802 11.5013L2.87994 11.5547L2.87929 11.554C2.46171 11.7898 1.96861 11.8532 1.50494 11.7292C1.0358 11.6037 0.635712 11.2972 0.392306 10.877L0.245171 10.6237C0.00252942 10.2031 -0.0631794 9.70286 0.0622282 9.23372C0.187015 8.76706 0.490489 8.36814 0.90728 8.12435L1.00754 8.06445L1.0147 8.06055C1.14187 7.98713 1.24752 7.88122 1.32069 7.75391C1.39386 7.62659 1.43195 7.48213 1.43137 7.33529V6.99609C1.43045 6.85083 1.39172 6.70829 1.31874 6.58268C1.24565 6.45697 1.14063 6.35266 1.0147 6.27995C1.0054 6.27458 0.996294 6.26833 0.987358 6.26237L0.887749 6.19596C0.481471 5.95121 0.184965 5.55861 0.0622282 5.09961C-0.0631795 4.63048 0.00252948 4.13093 0.245171 3.71029L0.392306 3.45638C0.635712 3.03615 1.0358 2.72964 1.50494 2.60417C1.96851 2.48021 2.46177 2.54305 2.87929 2.77865L2.9802 2.83203L2.99452 2.84049C3.12113 2.91359 3.265 2.95178 3.41119 2.95182C3.55736 2.95182 3.70124 2.91353 3.82785 2.84049L4.11366 2.67448L4.11496 2.67383C4.24147 2.60078 4.34653 2.49561 4.41965 2.36914C4.47448 2.27428 4.51012 2.16976 4.52447 2.06185L4.53163 1.95312V1.83333C4.53163 1.3471 4.72492 0.880926 5.06874 0.537109C5.41255 0.193349 5.87878 0 6.36496 0H6.65793C7.14412 0 7.61035 0.19335 7.95416 0.537109C8.29797 0.880926 8.49127 1.3471 8.49127 1.83333V1.95312Z"
      fill="currentColor"
    />
    <path
      d="M8.01145 7.16667C8.01145 6.33824 7.33988 5.66667 6.51145 5.66667C5.68302 5.66667 5.01145 6.33824 5.01145 7.16667C5.01145 7.99509 5.68302 8.66667 6.51145 8.66667C7.33988 8.66667 8.01145 7.99509 8.01145 7.16667ZM9.01145 7.16667C9.01145 8.54738 7.89216 9.66667 6.51145 9.66667C5.13074 9.66667 4.01145 8.54738 4.01145 7.16667C4.01145 5.78595 5.13074 4.66667 6.51145 4.66667C7.89216 4.66667 9.01145 5.78595 9.01145 7.16667Z"
      fill="currentColor"
    />
  </svg>
);

const CheckCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M14.1663 7.9987C14.1663 4.59294 11.4054 1.83203 7.99967 1.83203C4.59392 1.83203 1.83301 4.59294 1.83301 7.9987C1.83301 11.4045 4.59392 14.1654 7.99967 14.1654C11.4054 14.1654 14.1663 11.4045 14.1663 7.9987ZM15.1663 7.9987C15.1663 11.9567 11.9577 15.1654 7.99967 15.1654C4.04163 15.1654 0.833008 11.9567 0.833008 7.9987C0.833008 4.04066 4.04163 0.832031 7.99967 0.832031C11.9577 0.832031 15.1663 4.04066 15.1663 7.9987Z"
      fill="currentColor"
    />
    <path
      d="M9.64616 6.31185C9.84142 6.11659 10.1579 6.11659 10.3532 6.31185C10.5485 6.50711 10.5485 6.82362 10.3532 7.01888L7.68652 9.68555C7.49126 9.88081 7.17475 9.88081 6.97949 9.68555L5.64616 8.35221C5.4509 8.15695 5.4509 7.84044 5.64616 7.64518C5.84142 7.44992 6.15793 7.44992 6.35319 7.64518L7.33301 8.625L9.64616 6.31185Z"
      fill="currentColor"
    />
  </svg>
);

// The row menu's two glyphs, per the design: a grey tick beside "Mark as read"
// and a red bin beside "Delete". Both sit in a 16px box so the labels line up.
const CheckIcon = () => (
  <span className="w-4 h-4 shrink-0 flex items-center justify-center">
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="9" viewBox="0 0 12 9" fill="none">
      <path
        d="M10.8131 0.146447C11.0084 -0.0488156 11.3249 -0.0488156 11.5201 0.146447C11.7154 0.341709 11.7154 0.658216 11.5201 0.853478L4.18681 8.18681C3.99155 8.38208 3.67504 8.38208 3.47978 8.18681L0.146447 4.85348C-0.0488156 4.65822 -0.0488156 4.34171 0.146447 4.14645C0.341709 3.95119 0.658216 3.95119 0.853478 4.14645L3.8333 7.12627L10.8131 0.146447Z"
        fill="#7C829A"
      />
    </svg>
  </span>
);

const TrashIcon = () => (
  <span className="w-4 h-4 shrink-0 flex items-center justify-center">
    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="15" viewBox="0 0 13 15" fill="none">
      <path
        d="M12.5 2.66667C12.7761 2.66667 13 2.89052 13 3.16667C13 3.44281 12.7761 3.66667 12.5 3.66667H0.5C0.223858 3.66667 0 3.44281 0 3.16667C0 2.89052 0.223858 2.66667 0.5 2.66667H12.5Z"
        fill="#EF4444"
      />
      <path
        d="M1.33333 12.5V3.16667C1.33333 2.89052 1.55719 2.66667 1.83333 2.66667C2.10948 2.66667 2.33333 2.89052 2.33333 3.16667V12.5C2.33333 12.6707 2.42369 12.8833 2.60352 13.0632C2.78334 13.243 2.99595 13.3333 3.16667 13.3333H9.83334C10.0041 13.3333 10.2167 13.243 10.3965 13.0632C10.5763 12.8833 10.6667 12.6707 10.6667 12.5V3.16667C10.6667 2.89052 10.8905 2.66667 11.1667 2.66667C11.4428 2.66667 11.6667 2.89052 11.6667 3.16667V12.5C11.6667 12.996 11.4237 13.45 11.1035 13.7702C10.7833 14.0904 10.3293 14.3333 9.83334 14.3333H3.16667C2.67072 14.3333 2.21666 14.0904 1.89648 13.7702C1.57631 13.45 1.33333 12.996 1.33333 12.5Z"
        fill="#EF4444"
      />
      <path
        d="M8.66667 3.16667V1.83333C8.66667 1.66262 8.57631 1.45001 8.39649 1.27018C8.21666 1.09036 8.00405 1 7.83333 1H5.16667C4.99595 1 4.78334 1.09036 4.60352 1.27018C4.42369 1.45001 4.33333 1.66262 4.33333 1.83333V3.16667C4.33333 3.44281 4.10948 3.66667 3.83333 3.66667C3.55719 3.66667 3.33333 3.44281 3.33333 3.16667V1.83333C3.33333 1.33738 3.57631 0.883324 3.89649 0.563151C4.21666 0.242978 4.67072 0 5.16667 0H7.83333C8.32928 0 8.78334 0.242978 9.10352 0.563151C9.42369 0.883324 9.66667 1.33738 9.66667 1.83333V3.16667C9.66667 3.44281 9.44281 3.66667 9.16667 3.66667C8.89053 3.66667 8.66667 3.44281 8.66667 3.16667Z"
        fill="#EF4444"
      />
      <path
        d="M4.66667 10.5V6.5C4.66667 6.22386 4.89053 6 5.16667 6C5.44281 6 5.66667 6.22386 5.66667 6.5V10.5C5.66667 10.7761 5.44281 11 5.16667 11C4.89053 11 4.66667 10.7761 4.66667 10.5Z"
        fill="#EF4444"
      />
      <path
        d="M7.33333 10.5V6.5C7.33333 6.22386 7.55719 6 7.83333 6C8.10948 6 8.33334 6.22386 8.33334 6.5V10.5C8.33334 10.7761 8.10948 11 7.83333 11C7.55719 11 7.33333 10.7761 7.33333 10.5Z"
        fill="#EF4444"
      />
    </svg>
  </span>
);

// Type filter above the list. Options come from the renderer map, so a type
// this build cannot draw is never offered as something to filter by.
const TypeFilter = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const selected = NOTIFICATION_TYPE_OPTIONS.find((option) => option.value === value);

  const pick = (next) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="px-3 py-2 flex items-center gap-2 rounded-md border-[1px] border-[#E5E8F3] bg-white font-OpenSansSemiBold text-[14px] leading-[20px] text-[#1A2332]"
      >
        {selected ? selected.label : "All types"}
        <ChevronDown size={14} className="text-[#5F6477]" />
      </button>

      {open && (
        <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
          <div className={`absolute right-0 top-[44px] z-20 w-[240px] px-1 py-1 flex flex-col gap-[2px] ${MENU_SURFACE}`}>
            {[{ value: ALL_TYPES, label: "All types" }, ...NOTIFICATION_TYPE_OPTIONS].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => pick(option.value)}
                className={`${MENU_ITEM} ${
                  option.value === value ? "bg-[#F5F9FF] text-[#0065FD]" : "text-[#1A2332] hover:bg-[#F8F9FE]"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </OutsideClickHandler>
      )}
    </div>
  );
};

// Per-row actions. Clicks stop here rather than bubbling, so opening the menu
// never opens the work order behind it.
const RowMenu = ({ isUnread, onMarkRead, onRemove }) => {
  const [open, setOpen] = useState(false);

  const run = (action) => (event) => {
    event.stopPropagation();
    setOpen(false);
    action();
  };

  return (
    <div className="relative shrink-0" onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        aria-label="Notification actions"
        onClick={() => setOpen((prev) => !prev)}
        className="px-2 py-[9px] rounded-md text-[#868DA6] hover:text-[#1A2332]"
      >
        <MoreVertical size={16} />
      </button>

      {open && (
        <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
          <div className={`absolute right-0 top-[34px] z-20 w-[240px] px-1 py-1 flex flex-col gap-[2px] ${MENU_SURFACE}`}>
            {isUnread && (
              <button type="button" onClick={run(onMarkRead)} className={`${MENU_ITEM} text-[#1A2332] hover:bg-[#F8F9FE]`}>
                <CheckIcon />
                Mark as read
              </button>
            )}
            <button type="button" onClick={run(onRemove)} className={`${MENU_ITEM} text-[#EF4444] hover:bg-[#FEF2F2]`}>
              <TrashIcon />
              Delete
            </button>
          </div>
        </OutsideClickHandler>
      )}
    </div>
  );
};

// A page row: type label with the unread dot, the sentence, then the customer
// and the time on one meta line. Unread rows carry the blue tint and set the
// whole sentence in semibold; read rows drop to regular with only the names the
// renderer marks bold picked out.
const NotificationPageRow = ({ item, isLast, onClick, onMarkRead, onRemove }) => {
  const meta = item.meta || {};
  const renderer = getRenderer(item);
  const titleParts = renderer.title(meta) || [];
  const detail = renderer.detail(meta);
  const isUnread = !item.readAt;

  return (
    <div
      onClick={() => onClick(item)}
      className={`w-full p-5 flex items-start justify-between gap-2 cursor-pointer ${
        isLast ? "" : "border-b-[1px] border-[#EEF0F9]"
      } ${isUnread ? "bg-[#F5F9FF] hover:bg-[#EAF3FF]" : "bg-white hover:bg-[#F8F9FE]"}`}
    >
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span
            className={`text-[11px] leading-[16.5px] tracking-[0.55px] uppercase ${
              isUnread ? "font-OpenSansBold text-[#0065FD]" : "font-OpenSansSemiBold text-[#7C829A]"
            }`}
          >
            {renderer.label}
          </span>
          {isUnread && <span className="w-[6px] h-[6px] rounded-full bg-[#0065FD]" />}
        </div>

        <div className="text-[14px] leading-[21px] text-[#1A2332]">
          {titleParts.map((part, index) => (
            <span
              key={index}
              className={isUnread || part.bold ? "font-OpenSansSemiBold" : "font-OpenSansRegular"}
            >
              {part.text}
            </span>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {detail && (
            <>
              <div className="flex items-center gap-1 min-w-0">
                <Building2 size={12} className="shrink-0 text-[#868DA6]" />
                <span className="font-OpenSansRegular text-[12px] leading-[18px] text-[#5F6477] truncate">
                  {detail}
                </span>
              </div>
              <span className="font-OpenSansRegular text-[12px] leading-[18px] text-[#5F6477]">&bull;</span>
            </>
          )}
          <span className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#7C829A] whitespace-nowrap">
            {formatRelativeTime(item.createdAt)}
          </span>
        </div>
      </div>

      <RowMenu isUnread={isUnread} onMarkRead={() => onMarkRead(item)} onRemove={() => onRemove(item)} />
    </div>
  );
};

// Design's window: the first pages, an ellipsis, then the last one.
const buildPages = (current, total) => {
  if (total <= 5) return Array.from({ length: total }, (_, index) => index + 1);
  if (current <= 3) return [1, 2, 3, "...", total];
  if (current >= total - 2) return [1, "...", total - 2, total - 1, total];
  return [1, "...", current, "...", total];
};

// The card's footer: the range sentence on the left, Previous / pages / Next on
// the right. Bespoke rather than the shared Pagination because this one is
// spelled out in the design down to the 40px squares.
const ListPagination = ({ currentPage, totalPages, totalItems, rangeStart, rangeEnd, onPageChange }) => {
  const step = (page) => () => {
    const next = Math.min(Math.max(page, 1), totalPages);
    if (next !== currentPage) onPageChange(next);
  };

  const edgeButton = (label, targetPage, disabled) => (
    <button
      type="button"
      onClick={step(targetPage)}
      disabled={disabled}
      className={`px-[14px] py-2 rounded-lg border-[1px] bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] font-OpenSansSemiBold text-[14px] leading-[20px] ${
        disabled ? "border-[#E5E8F3] text-[#C9CEE2] cursor-not-allowed" : "border-[#C9CEE2] text-[#1A2332]"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="w-full px-6 pt-3 pb-4 flex items-center justify-between gap-4 border-t-[1px] border-[#E5E8F3]">
      <p className="font-OpenSansRegular text-[14px] leading-[20px] text-[#868DA6]">
        Showing <span className="font-OpenSansSemiBold text-[#1A2332]">{rangeStart}</span> to{" "}
        <span className="font-OpenSansSemiBold text-[#1A2332]">{rangeEnd}</span> of{" "}
        <span className="font-OpenSansSemiBold text-[#1A2332]">{totalItems}</span> notifications
      </p>

      <div className="flex items-center gap-3">
        {edgeButton("Previous", currentPage - 1, currentPage <= 1)}

        <div className="flex items-center gap-[2px]">
          {buildPages(currentPage, totalPages).map((entry, index) =>
            entry === "..." ? (
              <span
                key={`gap-${index}`}
                className="w-10 h-10 flex items-center justify-center font-OpenSansSemiBold text-[14px] leading-[20px] text-[#868DA6]"
              >
                ...
              </span>
            ) : (
              <button
                key={entry}
                type="button"
                onClick={step(entry)}
                aria-current={entry === currentPage ? "page" : undefined}
                className={`w-10 h-10 rounded-lg flex items-center justify-center font-OpenSansSemiBold text-[14px] leading-[20px] ${
                  entry === currentPage ? "bg-[#F5F9FF] text-[#0065FD]" : "text-[#868DA6] hover:bg-[#F8F9FE]"
                }`}
              >
                {entry}
              </button>
            )
          )}
        </div>

        {edgeButton("Next", currentPage + 1, currentPage >= totalPages)}
      </div>
    </div>
  );
};

/**
 * The full inbox behind the drawer's "View all notifications". Same rows and
 * same endpoints as the drawer, with the filtering and paging the 10-row panel
 * has no space for. `list` returns the newest 100 rows, so the paging here is
 * over what's already loaded rather than a server round trip per page.
 */
const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [type, setType] = useState(ALL_TYPES);
  const [page, setPage] = useState(1);

  const unreadCount = notifications.filter((item) => !item.readAt).length;

  const loadNotifications = useCallback(async () => {
    try {
      const res = await UserNotificationService.list();
      setNotifications((res?.data ?? []).filter(isRenderable));
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Rows arriving while the page is open are pulled in immediately. No
  // unsubscribe on cleanup: the socket is a shared singleton and the room
  // belongs to the connection, so leaving it would also cut off the bell.
  useEffect(() => {
    const socket = connectUserNotificationSocket();
    const subscribe = () => socket.emit("user-notifications:subscribe");
    const handleChanged = () => loadNotifications();

    socket.on("connect", subscribe);
    socket.on("user-notification-changed", handleChanged);
    if (socket.connected) subscribe();

    return () => {
      socket.off("connect", subscribe);
      socket.off("user-notification-changed", handleChanged);
      disconnectUserNotificationSocket();
    };
  }, [loadNotifications]);

  const filtered = useMemo(
    () =>
      notifications.filter(
        (item) => (tab === "all" || !item.readAt) && (type === ALL_TYPES || item.type === type)
      ),
    [notifications, tab, type]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  // Reading the last unread row on page 3 of the Unread tab shortens the list
  // under the cursor; without this the page would sit past the end and look
  // empty.
  const currentPage = Math.min(page, totalPages);
  const rangeStart = filtered.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, filtered.length);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const changeFilter = (apply) => {
    apply();
    setPage(1);
  };

  // Optimistic like the drawer: read state is not worth blocking the UI over,
  // and the next load corrects any failure. The bell is told to re-read once the
  // write lands — asking any earlier just re-reads the count we are changing.
  const markRead = (item) => {
    if (item.readAt) return;
    setNotifications((prev) =>
      prev.map((row) => (row.id === item.id ? { ...row, readAt: new Date().toISOString() } : row))
    );
    UserNotificationService.markRead(item.id)
      .catch(() => {})
      .finally(requestUnreadCountRefresh);
  };

  const handleMarkAllRead = () => {
    const now = new Date().toISOString();
    setNotifications((prev) => prev.map((row) => (row.readAt ? row : { ...row, readAt: now })));
    UserNotificationService.markAllRead()
      .catch(() => {})
      .finally(requestUnreadCountRefresh);
  };

  const handleRemove = (item) => {
    setNotifications((prev) => prev.filter((row) => row.id !== item.id));
    UserNotificationService.dismiss(item.id)
      .catch(handleError)
      .finally(requestUnreadCountRefresh);
  };

  const handleRowClick = (item) => {
    markRead(item);
    const path = getNotificationPath(item);
    if (path) navigate(path);
  };

  return (
    <div className="w-full h-full overflow-y-auto px-5">
      <div className="mx-auto w-full max-w-[900px]">
        <div className="pt-8 pb-4 flex items-center justify-between gap-4">
          {unreadCount > 0 ? (
            <span className="px-2 py-[2px] rounded-full bg-[#EAF3FF] font-InterBold text-[12px] leading-[18px] text-[#0065FD]">
              {unreadCount} unread
            </span>
          ) : (
            <span />
          )}

          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleMarkAllRead}
              disabled={unreadCount === 0}
              className="h-[32px] px-1 flex items-center gap-2 rounded-md text-[#0065FD] disabled:opacity-40"
            >
              <CheckCircleIcon />
              <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize">
                Mark all as read
              </span>
            </button>

            <button
              type="button"
              aria-label="Notification settings"
              onClick={() => navigate("/notification-settings")}
              className="w-[32px] h-[32px] flex items-center justify-center rounded-lg border-[1px] border-[#C9CEE2] bg-white shadow-[0px_1px_2px_rgba(16,24,40,0.05)] text-[#1A2332] hover:bg-[#F8F9FE]"
            >
              <SettingsIcon />
            </button>
          </div>
        </div>

        <div className="pt-2 pb-4">
          <div className="flex items-center justify-between gap-4 border-b-[1px] border-[#E5E8F3]">
            <div className="flex items-start">
              {TABS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => changeFilter(() => setTab(item.key))}
                  className={`p-4 border-b-[2px] -mb-[1px] font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-center capitalize ${
                    tab === item.key ? "text-[#0058E5] border-[#0058E5]" : "text-[#7C829A] border-transparent"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="pb-1">
              <TypeFilter value={type} onChange={(next) => changeFilter(() => setType(next))} />
            </div>
          </div>
        </div>

        <div className="pb-10">
          <div className="rounded-xl border-[1px] border-[#EEF0F9] bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
            {isLoading ? (
              <Loader variant="block" className="py-16" />
            ) : pageItems.length === 0 ? (
              <div className="py-16">
                <NotificationEmptyState />
              </div>
            ) : (
              pageItems.map((item, index) => (
                <NotificationPageRow
                  key={item.id}
                  item={item}
                  isLast={index === pageItems.length - 1}
                  onClick={handleRowClick}
                  onMarkRead={markRead}
                  onRemove={handleRemove}
                />
              ))
            )}

            {filtered.length > 0 && (
              <ListPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filtered.length}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                onPageChange={setPage}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
